/**
 * Fetches the moderated submissions/geolocation CSVs and the postcode
 * bbox GeoJSON, transforms them into a single GeoJSON FeatureCollection,
 * and pushes it into GeoJsonDataContext for Map/Table/Header to consume.
 * Renders nothing itself — it's a data-loading side effect mounted once
 * from App.js. See csvToGeoJSON for the actual CSV -> GeoJSON pipeline.
 */
import { useEffect } from "react";

import Papa from "papaparse";
import { useGeoJsonData } from "./GeoJsonDataContext";
import {
  MESSAGES_DATA_URL,
  BBOX_DATA_URL,
  GEOLOCATION_DATA_URL,
  NSW_BOUNDARY_URL,
  SCHOOL_LOCATIONS_URL,
  FIELD_PREFIX,
  UTF16,
  SPECIAL_FEATURES,
} from "./settings";

export const phyllotaxis_points = (num_points, center, spacing) => {
  let results = [];
  for (let i = 1; i <= num_points; i++) {
    let angle = i * (Math.PI * (3 - Math.sqrt(5)));
    // Distance counts from zero so the first gem sits on the anchor itself.
    // Starting at sqrt(1) pushed a lone gem a full cluster radius off its
    // school — 3.3km away, with nothing at the school at all.
    let distance = spacing * Math.sqrt(i - 1);
    let x = center[0] + Math.cos(angle) * distance;
    let y = center[1] + Math.sin(angle) * distance;
    results.push([x, y]);
  }
  return results;
};

export const calculateBBox = (polygon) => {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  polygon[0].forEach((coord) => {
    if (coord[0] < minX) minX = coord[0];
    if (coord[0] > maxX) maxX = coord[0];
    if (coord[1] < minY) minY = coord[1];
    if (coord[1] > maxY) maxY = coord[1];
  });
  return [minX, minY, maxX, maxY];
};

export const bboxRadius = (bbox) => {
  const [minX, minY, maxX, maxY] = bbox;
  return Math.sqrt((maxX - minX) ** 2 + (maxY - minY) ** 2) / 2;
};

// const getPresetMessage = (ind) => {
//   if(ind >= PRESET_SHOUTOUTS.length) return null;
//   return PRESET_SHOUTOUTS[ind];
// }

export const constructGems = (gems) => {
  const gemsArray = gems.split(";");
  // Remove semicolons from each word
  const words = gemsArray
    .filter((word) => word.trim() !== "")
    .map((gem) => gem.replace(/;/g, ""));
  switch (words.length) {
    case 0: // No words survived filtering (e.g. empty input)
      return "";
    case 1: // When there's only one word in the list
      return words[0];
    case 2: // When there are two words in the list
      return words.join(" and ");
    default: // For three or more words in the list
      const lastWord = words.pop(); // Remove and save the last word from the list
      return `${words.join(", ")}, and ${lastWord}`; // Join remaining words with commas, then append the last word with 'and' in front
  }
};

export const lowerCaseFirstLetter = (sentence) => {
  if (!sentence || typeof sentence !== "string") {
    return ""; // or you can return an error message
  }
  return sentence.charAt(0).toLowerCase() + sentence.slice(1);
};

export const constructMessage = (preset, teacherTitle, teacherName, custom) => {
  const salutation = teacherTitle + " " + teacherName;
  const gems = constructGems(preset);
  const cleanedCustom = custom.replace(/�/g, "'");
  if (preset && custom) {
    // debugger
    return salutation + " " + gems + ". " + cleanedCustom;
  } else if (preset && !cleanedCustom) {
    return salutation + " " + gems + ".";
  } else if (!preset && cleanedCustom) {
    return salutation + ", " + lowerCaseFirstLetter(cleanedCustom);
  } else {
    return null;
  }
};

/** Days since the Excel / Microsoft Forms epoch, which is 1899-12-30. */
const EXCEL_SERIAL_PATTERN = /^\d+(\.\d+)?$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const convertDateStringToDate = (dateString) => {
  const value = String(dateString).trim();

  // Microsoft Forms exports sometimes serialise timestamps as Excel numbers
  // ("46276.62163194444") rather than "10/26/25 14:55:09": whole days since
  // 1899-12-30, with the fraction as time of day. This used to fall through to
  // the split below, where the missing time part threw and took the whole
  // submission with it — message, school and all.
  if (EXCEL_SERIAL_PATTERN.test(value)) {
    const epoch = new Date(1899, 11, 30, 0, 0, 0, 0); // local, so it displays as submitted
    return new Date(epoch.getTime() + Number(value) * MS_PER_DAY);
  }

  // Split the date and time parts
  const [datePart, timePart] = value.split(" ");

  // Two shapes occur in real exports:
  //   "10/26/25 14:55:09"  older export — month first, two-digit year
  //   "14/09/2026 16:44"   2026 Forms export — day first, four-digit year,
  //                        no seconds
  // A four-digit year marks the newer shape; a first component above 12 can
  // only be a day. Guessing wrong silently produced dates ~2000 years out
  // ("-730690 days ago" in the table) because 2026 + 2000 was used as the year.
  const [firstPart, secondPart, yearPart] = datePart.split("/");
  const hasFourDigitYear = (yearPart || "").length === 4;
  const dayFirst = hasFourDigitYear || parseInt(firstPart, 10) > 12;

  const day = parseInt(dayFirst ? firstPart : secondPart, 10);
  const month = parseInt(dayFirst ? secondPart : firstPart, 10);
  const year = hasFourDigitYear
    ? parseInt(yearPart, 10)
    : parseInt(yearPart, 10) + 2000; // two-digit years are 20xx

  // Split and destructure the time part. A date with no time is valid; it just
  // means midnight, and must not discard the submission.
  const [hour, minute, second] = (timePart || "").split(":");

  // Create a new Date object
  // Note: JavaScript months are zero-based, so we need to subtract 1 from the month
  return new Date(
    year,
    month - 1,
    day,
    parseInt(hour, 10) || 0,
    parseInt(minute, 10) || 0,
    parseInt(second, 10) || 0
  );
};

/**
 * Gems used to be spread across a postcode's bounding box: spacing was
 * radius / sqrt(n), and rural postcodes are enormous. Postcode 2840 (Bourke)
 * has a ~265km radius, so four gems landed 133-265km from its centre, one of
 * them over the Queensland border. The postcode centroid is a poor anchor too
 * — 2840's centroid is ~110km from Bourke township.
 *
 * Gems are now placed on the school itself where its coordinates are known,
 * fanned out by ~130m so several at one school stay distinguishable but read
 * as the same place. Schools with no known location still fall back to the
 * postcode centroid, but with the spread capped at ~2km rather than scaled to
 * the postcode.
 */
// Gap between neighbouring gems in a cluster. Fixed spacing (rather than a
// fixed cluster radius) lets the fan grow with the number of gems, which is
// what gave the original its spread-out look: 46 gems at Euston fan out ~15km,
// four at Bourke ~4km. A fixed radius did the opposite — the more gems a school
// collected, the tighter they packed.
const GEM_SPACING_DEGREES = 0.09; // ~10km between neighbours
// Ceiling so a very popular school stays recognisably in one place. The old
// code had none: Bourke's postcode spread gems 265km, into Queensland.
const MAX_CLUSTER_RADIUS_DEGREES = 0.5; // ~55km

/**
 * Ray-casting point-in-polygon, walking a GeoJSON (Multi)Polygon. A point
 * counts as inside when it falls in an outer ring and in none of that
 * polygon's holes — the ACT is a hole in NSW.
 */
const ringContains = (ring, [x, y]) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[j];
    if ((y1 > y) !== (y2 > y) && x < ((x2 - x1) * (y - y1)) / (y2 - y1) + x1) {
      inside = !inside;
    }
  }
  return inside;
};

export const isInsideNsw = (point, boundary) => {
  if (!boundary) return true; // no outline loaded: place gems as calculated
  const geometry = boundary.features ? boundary.features[0].geometry : boundary;
  const polygons =
    geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  return polygons.some(
    (rings) =>
      ringContains(rings[0], point) &&
      !rings.slice(1).some((hole) => ringContains(hole, point))
  );
};

/**
 * Pull a gem back along its own bearing until it sits on NSW land. Gems fan out
 * around a school, so a coastal or border school could otherwise throw them into
 * the sea or into Victoria. Falls back to the anchor, which is the school itself.
 */
const clampToNsw = (point, anchor, boundary) => {
  if (isInsideNsw(point, boundary)) return point;
  for (let scale = 0.6; scale > 0.05; scale *= 0.6) {
    const pulled = [
      anchor[0] + (point[0] - anchor[0]) * scale,
      anchor[1] + (point[1] - anchor[1]) * scale,
    ];
    if (isInsideNsw(pulled, boundary)) return pulled;
  }
  return anchor;
};

/** Compare school names ignoring case, punctuation and spacing. */
export const normaliseSchoolName = (name) =>
  (name || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

export const csvToGeoJSON = (
  messages,
  geolocation,
  bboxPolygons,
  schoolLocations,
  nswBoundary
) => {
  const messagesData = Papa.parse(messages, {
    header: true,
    skipEmptyLines: true,
    transformHeader: function (header) {
      // Removing newline characters
      var cleanedHeader = header.replace(/\r\n/g, "").trim();
      return cleanedHeader;
    },
  }).data;
  const geolocationData = Papa.parse(geolocation, {
    header: true,
    skipEmptyLines: true,
  }).data;

  const schoolCoordinates = new Map();
  if (schoolLocations) {
    Papa.parse(schoolLocations, { header: true, skipEmptyLines: true }).data.forEach(
      (school) => {
        const longitude = parseFloat(school.longitude);
        const latitude = parseFloat(school.latitude);
        if (!Number.isNaN(longitude) && !Number.isNaN(latitude)) {
          schoolCoordinates.set(normaliseSchoolName(school.school_name), [
            longitude,
            latitude,
          ]);
        }
      }
    );
  }

  const getSchoolName = function (entry) {
    return entry[FIELD_PREFIX + "Corrected School Name"]
      ? entry[FIELD_PREFIX + "Corrected School Name"].trim()
      : entry[FIELD_PREFIX + "School name"].trim();
  };

  const validateSchoolName = function (name) {
    // if (name === "2190") debugger;
    const isNumber = /^\d+$/.test(name);
    const hasAlphabetChars = /[a-zA-Z]/.test(name);
    return !isNumber || hasAlphabetChars;
  };
  const jsonData = messagesData
    .filter((entry) => {
      try {
        const safe =
          entry[FIELD_PREFIX + "Classification"].toLowerCase().trim() ===
          "safe";
        const publish =
          entry[FIELD_PREFIX + "Publish?"].toLowerCase().trim() === "y";
        const validSchoolName = validateSchoolName(getSchoolName(entry));
        return validSchoolName && safe && publish;
      } catch (error) {
        // A row missing one of the required columns above (e.g. a
        // malformed export) would otherwise throw and take down the
        // entire map with it. Drop just that row instead.
        console.warn("Skipping malformed submission row:", error, entry);
        return false;
      }
    })
    .map((entry, index) => {
      try {
        const id = index;
        // debugger
        const postcodeID =
          entry[
            FIELD_PREFIX +
              "To put your message on a map, what's the postcode for the school?"
          ].trim();
        const location = postcodeID
          ? geolocationData.find(
              (location) => location[FIELD_PREFIX + "POA_CODE21"] === postcodeID
            )
          : null;
        const school = getSchoolName(entry);
        // const presetshoutout = entry[FIELD_PREFIX+"presetshoutout"];
        const schoollevel = entry[FIELD_PREFIX + "Teacher's school level"].trim();
        const custom =
          entry[FIELD_PREFIX + "Write a custom message (optional)"].trim();
        const preset = entry[FIELD_PREFIX + "My teaching gem (teacher)"].trim();
        const teacherTitle = entry[FIELD_PREFIX + "Teacher's title"].trim();
        const teacherName = entry[FIELD_PREFIX + "Teacher's initial"].trim();

        const date = entry[FIELD_PREFIX + "Completion time"].trim();

        return {
          id,
          postcode: location ? location[FIELD_PREFIX + "POA_CODE21"] : null,
          unique_id: id,
          longitude: location
            ? parseFloat(location[FIELD_PREFIX + "longitude"])
            : null,
          latitude: location
            ? parseFloat(location[FIELD_PREFIX + "latitude"])
            : null,
          message: constructMessage(preset, teacherTitle, teacherName, custom), // add presetshoutout
          school,
          schoollevel,
          date: date ? convertDateStringToDate(date) : null,
        };
      } catch (error) {
        console.warn("Skipping malformed submission row:", error, entry);
        return null;
      }
    })
    .filter((entry) => entry !== null);
  // Group by postcode
  const groupedEntries = {};

  jsonData.forEach((entry) => {
    const schoolKey = normaliseSchoolName(entry.school);
    const schoolPoint = schoolCoordinates.get(schoolKey);
    entry.schoolPoint = schoolPoint || null;

    // Gems at the same school belong together; everything else still groups by
    // postcode so it lands on that centroid.
    const key = schoolPoint
      ? `school-${schoolKey}`
      : entry.postcode
      ? `${entry.postcode}-${entry.latitude}-${entry.longitude}`
      : "No location";
    if (!groupedEntries[key]) {
      groupedEntries[key] = [];
    }
    groupedEntries[key].push(entry);
  });

  const features = SPECIAL_FEATURES.map((special) => ({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: special.coordinates,
    },
    properties: {
      id: special.id,
      message: special.message,
      school: special.school,
      schoollevel: special.id,
      date: convertDateStringToDate(special.date),
    },
  }));

  // eslint-disable-next-line no-unused-vars
  for (const [_, entries] of Object.entries(groupedEntries)) {
    const [firstEntry] = entries;
    const center = firstEntry.schoolPoint || [
      parseFloat(firstEntry.longitude),
      parseFloat(firstEntry.latitude),
    ];

    // phyllotaxis_points puts the furthest gem at spacing * sqrt(n), so deriving
    // spacing from a target cluster radius keeps the fan the same size whether a
    // school has three gems or thirty. A flat spacing made big clusters sprawl
    // and small ones collapse into a single dot at statewide zoom.
    // phyllotaxis_points puts the furthest gem at spacing * sqrt(n - 1).
    const steps = Math.sqrt(Math.max(entries.length - 1, 1));
    let spacing;
    if (firstEntry.schoolPoint) {
      spacing = Math.min(
        GEM_SPACING_DEGREES,
        MAX_CLUSTER_RADIUS_DEGREES / steps
      );
    } else {
      // Calculate the Phyllotaxis point positions for entries in this group
      const matchingPolygon = bboxPolygons.features.find((polygon) => {
        return polygon.properties.POA_CODE21 === firstEntry.postcode;
      });
      const bbox = matchingPolygon
        ? calculateBBox(matchingPolygon.geometry.coordinates)
        : null;
      const radius = bbox ? bboxRadius(bbox) : null;
      // Cap the radius, not the spacing: postcode 2840 spans ~265km, which used
      // to throw gems hundreds of kilometres apart.
      // Without a school location the postcode centroid is the anchor, so keep
      // the fan inside the postcode where its bbox is small, and inside the
      // ceiling where it is not.
      const clusterRadius = Math.min(
        radius || MAX_CLUSTER_RADIUS_DEGREES,
        MAX_CLUSTER_RADIUS_DEGREES
      );
      spacing = Math.min(GEM_SPACING_DEGREES, clusterRadius / steps);
    }

    const points = phyllotaxis_points(entries.length, center, spacing);

    points.forEach((point, index) => {
      const entry = entries[index];
      features.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: clampToNsw(point, center, nswBoundary),
        },
        properties: {
          ...entry,
          schoolPoint: undefined,
          schoollevel: parseInt(entry.schoollevel)
            ? parseInt(entry.schoollevel)
            : entry.schoollevel,
        },
      });
    });
  }

  const geoJson = {
    type: "FeatureCollection",
    features: features,
  };
  return geoJson;
};

/**
 * fetch() only rejects on network failure — a 403/404 resolves with ok=false.
 * Without this check an error page gets decoded as if it were CSV (silently
 * yielding zero rows) or parsed as JSON (throwing an opaque
 * "Unexpected token '<'"). Fail here instead, naming the URL and status.
 */
async function fetchResource(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch ${url} — ${response.status} ${response.statusText}`
    );
  }
  return response;
}

async function fetchData() {
  const [
    messagesResponse,
    geolocationResponse,
    polygonResponse,
    schoolLocationsResponse,
    nswBoundaryResponse,
  ] =
    await Promise.all([
      fetchResource(MESSAGES_DATA_URL),
      fetchResource(GEOLOCATION_DATA_URL),
      fetchResource(BBOX_DATA_URL),
      fetchResource(SCHOOL_LOCATIONS_URL),
      fetchResource(NSW_BOUNDARY_URL),
    ]);

  // Decoder for MS Power Apps CSV export
  const csvTextDecoder = new TextDecoder(UTF16 ? "utf-16" : "utf-8");

  // Messages
  const messagesArrayBuffer = await messagesResponse.arrayBuffer();
  const messages = csvTextDecoder.decode(messagesArrayBuffer);
  // Geolocation correspondences
  const geolocationArrayBuffer = await geolocationResponse.arrayBuffer();
  const geolocation = csvTextDecoder.decode(geolocationArrayBuffer);

  const bboxPolygons = await polygonResponse.json();
  // School coordinates
  const schoolLocationsArrayBuffer = await schoolLocationsResponse.arrayBuffer();
  const schoolLocations = csvTextDecoder.decode(schoolLocationsArrayBuffer);

  const nswBoundary = await nswBoundaryResponse.json();

  return { messages, geolocation, bboxPolygons, schoolLocations, nswBoundary };
}

export const DataLoaderHelper = () => {
  const { setGeoJsonData, setLoadError } = useGeoJsonData();

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        const {
          messages,
          geolocation,
          bboxPolygons,
          schoolLocations,
          nswBoundary,
        } = await fetchData();
        const geoJson = csvToGeoJSON(
          messages,
          geolocation,
          bboxPolygons,
          schoolLocations,
          nswBoundary
        );
        if (!cancelled) setGeoJsonData(geoJson);
      } catch (error) {
        // Previously this rejection was unhandled, so geoJsonData stayed null
        // and every consumer spun its loading spinner forever with no clue why.
        console.error("Failed to load map data:", error);
        if (!cancelled) setLoadError(error);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [setGeoJsonData, setLoadError]);

  return null; // This component does not render anything itself
};
